import React, { useContext, useEffect, useState, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Accordion, AccordionActions, AccordionDetails, AccordionSummary, Box, Breadcrumbs, Button, Checkbox, FormControl, FormControlLabel, InputLabel, MenuItem, Paper, Select, TextField, Tooltip, Typography } from '@mui/material'
import 'json-diff-kit/dist/viewer.css';
import AppContext from '../../state/AppContext';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import Header from '../../template/Header';
import { Editor } from '@monaco-editor/react';
import PlaylistAddCheckCircleIcon from '@mui/icons-material/PlaylistAddCheckCircle';
import APIInvoke from '../../utils/APIInvoke';
import toast from 'react-hot-toast';
import { hasValue } from '../../utils/StringUtils';
import ConfirmDialog from '../../template/ConfirmDialog';
import CircularProgress from '@mui/material/CircularProgress';
import GrantBreadcrumbs from '../../template/GrantBreadcrumbs';

const EditCollectionIndex = (props) => {

    const [state, setState] = useState({
        name: "",
        type: "",
        autoEvaluate: true,
        documentExample: "",
        expression: ""
    })
    const [testResult, setTestResult] = useState(null)
    const { collection, documentId, indexId } = useParams()
    const { appContext } = useContext(AppContext)
    const project = appContext.project
    const completionProviderRef = useRef(null);
    const navigate = useNavigate();
    const [showReindexDialog, setShowReindexDialog] = useState(false);

    useEffect(() => {
        if (!indexId) return;
        const loadIndex = async () => {
            const rawResponse = await APIInvoke.invokeGET(`/projects/${project}/collections/${collection}`)
            const response = await rawResponse.json();
            const index = response.body.indexes.find(index => index.id === indexId);
            if (index) {
                setState({
                    ...state,
                    ...index
                });
            }
        }
        loadIndex();
    }, [documentId])


    useEffect(() => {
        if (state.autoEvaluate) {
            validateExpression();
        }
    }, [state.documentExample, state.type, state.expression, state.autoEvaluate])

    const validateExpression = async () => {
        try {
            if (!hasValue(state.documentExample) || !hasValue(state.expression) || !hasValue(state.type)) {
                return;
            }

            let documentExample = null;
            try {
                documentExample = JSON.parse(state.documentExample);
            } catch (e) {
                return null;
            }


            const request = {
                expression: state.expression,
                type: state.type,
                documentExample: documentExample
            }

            const rawResponse = await APIInvoke.invokePOST(`/projects/${project}/collections/${collection}/indexes/test`, request);
            const response = await rawResponse.json();
            setTestResult(response)

        } catch (error) {
            console.error('Error parsing document example:', error);
        }
    }


    const onChangeExample = (newValue) => {
        setState({ ...state, documentExample: newValue })
    }

    const onChangeRule = (e) => {
        setState({ ...state, [e.target.name]: e.target.value })
    }

    const onSave = async () => {
        const request = {
            id: state.id,
            name: state.name,
            type: state.type,
            expression: state.expression,
            documentExample: state.documentExample
        }

        const rawResponse = state.id
            ? await APIInvoke.invokePUT(`/projects/${project}/collections/${collection}/indexes/${state.id}`, request)
            : await APIInvoke.invokePOST(`/projects/${project}/collections/${collection}/indexes`, request);

        const response = await rawResponse.json();

        if (response.ok) {
            setState({ ...state, id: response.body.id })
            toast.success("Index saved successfully")
            setShowReindexDialog(true);
        } else {
            toast.error(response.message)
        }
    }

    const backToCollection = () => {
        navigate(`/projects/${project}/collections/${collection}/edit?tab=indexes`)
    }

    const setupAutocompletion = (editor, monaco) => {
        // Remover el proveedor anterior si existe
        if (completionProviderRef.current) {
            completionProviderRef.current.dispose();
        }

        let suggestions = [];
        try {
            let jsonDoc = null;
            try {
                jsonDoc = JSON.parse(state.documentExample);
                suggestions = getNestedProperties(jsonDoc);
            } catch (error) {
                return;
            }

            // Función recursiva para obtener todas las propiedades anidadas
            const getNestedProperties = (obj, prefix = '') => {
                return Object.entries(obj).reduce((acc, [key, value]) => {
                    const fullPath = prefix ? `${prefix}.${key}` : key;
                    acc.push({
                        label: key,
                        kind: monaco.languages.CompletionItemKind.Property,
                        insertText: key,
                        detail: typeof value,
                        documentation: fullPath
                    });

                    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
                        acc.push(...getNestedProperties(value, fullPath));
                    }

                    return acc;
                }, []);
            };

            

            // Variables predefinidas
            const predefinedVariables = [
                {
                    label: '$',
                    kind: monaco.languages.CompletionItemKind.Variable,
                    insertText: '$',
                    detail: 'Current document',
                    documentation: 'This variable represents the current document'
                }
            ];

            // Registrar el nuevo proveedor y guardar la referencia
            completionProviderRef.current = monaco.languages.registerCompletionItemProvider('javascript', {
                triggerCharacters: ['$', '.'],
                provideCompletionItems: (model, position) => {
                    const textUntilPosition = model.getValueInRange({
                        startLineNumber: position.lineNumber,
                        startColumn: 1,
                        endLineNumber: position.lineNumber,
                        endColumn: position.column
                    });

                    // Mostrar variables predefinidas al inicio
                    if (textUntilPosition === '') {
                        return {
                            suggestions: predefinedVariables
                        };
                    }

                    // Solo mostrar sugerencias después de "$."
                    if (textUntilPosition.endsWith('$.')) {
                        return {
                            suggestions: suggestions.filter(s => !s.documentation.includes('.'))
                        };
                    }

                    // Si estamos en medio de una propiedad anidada (ej: $.contacto.)
                    const match = textUntilPosition.match(/\$\.([\w.]+)\.$/);
                    if (match) {
                        const currentPath = match[1];
                        const nestedSuggestions = suggestions.filter(s => {
                            const suggestionPath = s.documentation;
                            const pathParts = suggestionPath.split('.');
                            const currentParts = currentPath.split('.');

                            return suggestionPath.startsWith(currentPath + '.') &&
                                pathParts.length === currentParts.length + 1;
                        }).map(s => ({
                            ...s,
                            insertText: s.documentation.split('.').pop()
                        }));

                        return {
                            suggestions: nestedSuggestions
                        };
                    }

                    // Mostrar variables predefinidas después de un espacio o al inicio de una nueva línea
                    if (textUntilPosition.match(/^\s*$/) || textUntilPosition.endsWith(' ')) {
                        return {
                            suggestions: predefinedVariables
                        };
                    }

                    return { suggestions: [] };
                }
            });
        } catch (e) {
            console.error('Error parsing document example:', e);
        }
    }

    const handleEditorDidMount = (editor, monaco) => {
        setupAutocompletion(editor, monaco);
    }

    useEffect(() => {
        setupAutocompletion(null, window.monaco);
    }, [state.documentExample, window.monaco]);

    const fetchRandomDocumentExample = async () => {
        try {
            setState({ ...state, exampleLoading: true })
            const rawResponse = await APIInvoke.invokeGET(`/projects/${project}/collections/${collection}/documents/random`);
            const response = await rawResponse.json();
            onChangeExample(JSON.stringify(response.body.payload, null, 2))
        } catch (error) {
            setState({ ...state, exampleLoading: false })
            toast.error("Error fetching random document example")
        }
    }

    return (

        <div className="">
            <ConfirmDialog title="Warning" closeMessage="OK" color="orange" message="The new or update index are applied only to new documents. If you want to apply the index to existing documents, you need to reindex the collection." open={showReindexDialog} onClose={backToCollection} />
            <Header title={<span>{state?.id ? state?.name : "New index"}<span className="text-xs text-gray-700">/ {documentId} </span></span>} backLink={`/projects/${project}/collections/${collection}/edit?tab=indexes`} back={{ tab: "indexes" }} >
                <Breadcrumbs aria-label="breadcrumb" separator="/" className="!text-gray-600" >
                    <Typography ><HomeOutlinedIcon className="text-gray-600" fontSize="small" /></Typography>
                    <GrantBreadcrumbs permisos={[Permissions.COLLECTION_R]} text="Collections" link={`/projects/${project}/collections`} />
                    <GrantBreadcrumbs permisos={[Permissions.COLLECTION_W]} text={collection} link={`/projects/${project}/collections/${collection}/edit?tab=indexes`} />
                    <Typography ><span className="text-gray-600">{documentId}</span></Typography>
                </Breadcrumbs>
            </Header>

            <div className="content grid gap-8">

                <Accordion expanded={true} >
                    <AccordionSummary className="!bg-gray-100 border-b !cursor-default w-full" id={state.id}  >
                        <div className="w-full flex flex-row justify-between items-center">
                            <span>Collection Index</span>
                            <FormControl variant="outlined" size="small" >
                                <FormControlLabel
                                    labelPlacement="end"
                                    label="Auto evaluate"
                                    control={
                                        <Checkbox
                                            id="autoEvaluate"
                                            name="autoEvaluate"
                                            className="!p-0 !pr-2"
                                            checked={state.autoEvaluate}
                                            onChange={(e) => { setState({ ...state, autoEvaluate: e.target.checked }) }}
                                        />
                                    }
                                />
                            </FormControl>
                        </div>
                    </AccordionSummary>
                    <AccordionDetails className="p-4">
                        <div className="grid gap-4 py-4">

                            <div className="grid grid-cols-2 gap-4 ">
                                <Box className="h-full grid gap-4 ">
                                    <p>Document example</p>
                                    <Paper className="h-full overflow-hidden border p-4">
                                        <div className="w-full h-[350px] border">
                                            <Editor
                                                options={{
                                                    selectOnLineNumbers: true,
                                                    automaticLayout: true,
                                                    minimap: {
                                                        enabled: false
                                                    },
                                                    lineNumbers: 'on'
                                                }}
                                                width="100%"
                                                height="100%"
                                                language="json"
                                                theme="git"
                                                value={state.documentExample}
                                                onChange={onChangeExample}
                                            />
                                        </div>
                                        <p className="text-gray-700 text-xs px-4 py-2">Use <strong className="text-green-500">$</strong> to reference to this document in the expression field</p>
                                        <Button fullWidth loading disabled={state.exampleLoading} variant="contained" color="primary" className="h-14" onClick={fetchRandomDocumentExample} > {state.exampleLoading ? <CircularProgress size={16} className="!text-white" /> : "Get Random Example"} </Button>
                                    </Paper>
                                </Box>

                                <Box className="flex flex-col gap-4">
                                    <p>Index rule</p>
                                    <div className="flex flex-col gap-4 flex-1">
                                        <TextField
                                            required
                                            fullWidth
                                            disabled={state.id}
                                            label="Name"
                                            helperText="Name to use in the search"
                                            value={state.name}
                                            onChange={onChangeRule}
                                            name="name"
                                        />

                                        <FormControl size="medium" className="flex-1" required>
                                            <InputLabel id="type-label">Expected result type</InputLabel>
                                            <Select
                                                required
                                                labelId="type-label"
                                                id="type"
                                                name="type"
                                                value={state.type}
                                                label="Expected result type"
                                                onChange={onChangeRule}
                                            >
                                                <MenuItem value="STRING">String</MenuItem>
                                                <MenuItem value="NUMBER">Number</MenuItem>
                                                <MenuItem value="BOOLEAN">Boolean</MenuItem>
                                                <MenuItem value="DATE">Date</MenuItem>
                                                <MenuItem value="DATETIME">DateTime</MenuItem>
                                            </Select>
                                        </FormControl>

                                        <Paper className="h-full overflow-hidden">
                                            <div className="w-full h-[150px]">
                                                <Editor
                                                    required
                                                    options={{
                                                        selectOnLineNumbers: true,
                                                        automaticLayout: true,
                                                        minimap: {
                                                            enabled: false
                                                        },
                                                        lineNumbers: 'on',
                                                        scrollbar: {
                                                            vertical: 'auto',
                                                            horizontal: "auto",
                                                        },
                                                        ariaLabel: 'required',
                                                        placeholder: '$.field',
                                                    }}
                                                    width="100%"
                                                    height="100%"
                                                    language="javascript"
                                                    defaultLanguage="javascript"
                                                    theme="light"
                                                    className="h-full"
                                                    value={state.expression}
                                                    onChange={(newValue) => setState({ ...state, expression: newValue })}
                                                    onMount={handleEditorDidMount}
                                                />
                                            </div>
                                        </Paper>
                                    </div>
                                    {testResult && <p className={testResult?.body?.valid ? "bg-green-500 p-2 text-sm rounded-sm" : "bg-red-500 p-2 text-sm rounded-sm"}>{testResult?.body?.message}</p>}
                                </Box>
                            </div>
                        </div>
                    </AccordionDetails>
                    <AccordionActions className="bg-gray-100 border-t w-full">
                        <div className="flex flex-row items-center justify-between w-full">
                            <div className="flex flex-row gap-4 justify-end w-full">
                                <Tooltip title="Test rule">
                                    <Button variant="contained" color="primary" className="" disabled={state.autoEvaluate} onClick={() => validateExpression()} >
                                        <PlaylistAddCheckCircleIcon />
                                        <span className="ml-1 text-white">EVALUATE</span>
                                    </Button>
                                </Tooltip>
                                <Tooltip title="Delete rule">
                                    <Button variant="contained" color="primary" className="" onClick={() => onSave()} ><span className="ml-1 text-white">SAVE</span></Button>
                                </Tooltip>
                            </div>
                        </div>
                    </AccordionActions>
                </Accordion>
            </div>
        </div>
    )
}

export default EditCollectionIndex